// F15a region capture (FR-RD1): the desktop/browser grabs the whole display, and the
// compositor crops it on a canvas. Pure math so it is unit-testable without a DOM.
// Mirrors clients/desktop/src/capture/region.rs (even output for H.264 4:2:0).

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RegionCrop {
  /** Source rect in the captured video's pixels (drawImage sx, sy, sw, sh). */
  source: Rect;
  /** Output canvas size: the region size in video pixels, rounded down to even. */
  canvasWidth: number;
  canvasHeight: number;
}

export const MIN_REGION_SIDE = 16;

export function evenFloor(v: number): number {
  return Math.max(2, Math.floor(v) & ~1);
}

/**
 * Map a region selected in display-logical pixels onto a captured frame.
 * `videoWidth/Height` are the frame's real pixels (DPI-scaled); `displayWidth/Height` are the
 * logical size the picker used. The region is clamped to the frame.
 */
export function computeRegionCrop(
  region: Rect,
  videoWidth: number,
  videoHeight: number,
  displayWidth: number,
  displayHeight: number,
): RegionCrop | null {
  if (videoWidth <= 0 || videoHeight <= 0 || displayWidth <= 0 || displayHeight <= 0) return null;
  const sx = videoWidth / displayWidth;
  const sy = videoHeight / displayHeight;
  const x0 = Math.max(0, Math.round(region.x * sx));
  const y0 = Math.max(0, Math.round(region.y * sy));
  const x1 = Math.min(videoWidth, Math.round((region.x + region.width) * sx));
  const y1 = Math.min(videoHeight, Math.round((region.y + region.height) * sy));
  const w = x1 - x0;
  const h = y1 - y0;
  if (w < MIN_REGION_SIDE || h < MIN_REGION_SIDE) return null;
  const canvasWidth = evenFloor(w);
  const canvasHeight = evenFloor(h);
  return {
    source: { x: x0, y: y0, width: canvasWidth, height: canvasHeight },
    canvasWidth,
    canvasHeight,
  };
}

/** Parse the `region:x,y,w,h` source id shared with the desktop shell. */
export function parseRegionSourceId(id: string): Rect | null {
  const m = /^region:\s*(-?\d+)\s*,\s*(-?\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*$/.exec(id);
  if (!m) return null;
  const [x, y, width, height] = m.slice(1).map(Number);
  if (width < MIN_REGION_SIDE || height < MIN_REGION_SIDE) return null;
  return { x, y, width, height };
}

export function regionSourceId(r: Rect): string {
  return `region:${Math.round(r.x)},${Math.round(r.y)},${Math.round(r.width)},${Math.round(r.height)}`;
}
